import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkForUpdate, reloadToLatest } from './version-check';
import './update-banner.css';

// How often to re-check for a new build (ms), by context:
//  - Login / public screens poll frequently: the check auto-reloads, so we want
//    to catch a deploy quickly, before the user logs in on a stale build.
//  - Inside the app we poll rarely: it only shows a passive banner on a build
//    that already works, so there's no urgency — this avoids hammering the
//    server every few seconds for a user who keeps the app open all day.
// (mount + tab-focus checks run in BOTH cases, so returning to the tab is instant.)
// Note: these intervals are only used to throttle the checks, not to schedule
const LOGIN_POLL_MS = 20000;        // 20s on login / public routes
const IN_APP_POLL_MS = 5 * 60000;   // 5 min while logged in the app

// Routes where we auto-reload instead of prompting (public / pre-login pages).
const PUBLIC_ROUTES = ['/login', '/new-password'];

/**
 * Detects a newly-deployed build and prompts the user to refresh onto it.
 *
 *  - Login / public routes: shows an "updating" toast and auto-reloads after a
 *    short delay, so the user logs in on the fresh build.
 *  - Inside the app (logged in): shows a dismissible banner with a manual
 *    Reload, so nothing reloads under the user's unsaved work.
 *
 * Checks on mount, on tab focus, and on an interval — so a deploy that lands
 * while the user is sitting on the login screen is still caught and reloaded.
 * No-op in dev / when already on the latest build (see version-check.js).
 * Self-contained: App only needs to render it once.
 */
const VersionUpdateNotifier = () => {
    const isLoggedIn = useSelector((state) => !!(state.auth && state.auth.user));
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [autoReloading, setAutoReloading] = useState(false);
    const [latestVersion, setLatestVersion] = useState(null);

    useEffect(() => {
        const onPublicRoute = () => {
            const path = (typeof window !== 'undefined' && window.location.pathname) || '';
            return PUBLIC_ROUTES.indexOf(path) !== -1;
        };

        // The actual check. Records when it last ran so event-triggered checks
        // can be throttled (see maybeCheck).
        let lastCheckAt = 0;
        const check = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            lastCheckAt = Date.now();
            if (onPublicRoute()) {
                // Login / public screen: show the toast and auto-reload.
                checkForUpdate({
                    autoReload: true,
                    delayMs: 1500,
                    onStale: () => setAutoReloading(true),
                });
            } else {
                // Inside the app: prompt only (never yank the page from the user).
                checkForUpdate({
                    onStale: (v) => { setLatestVersion(v); setUpdateAvailable(true); },
                });
            }
        };

        // Throttled entry point for focus / visibility events: these fire often
        // during normal use (switching windows, clicking with DevTools open,
        // focusing inputs), so only actually check if a full poll interval has
        // elapsed since the last check. Prevents a burst of version.json fetches.
        const maybeCheck = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            const minGap = onPublicRoute() ? LOGIN_POLL_MS : IN_APP_POLL_MS;
            if (Date.now() - lastCheckAt < minGap) return;
            check();
        };

        // Self-rescheduling timer so the cadence follows the current route:
        // fast (20s) on login/public screens, slow (5 min) once inside the app.
        let timerId;
        const schedule = () => {
            const delay = onPublicRoute() ? LOGIN_POLL_MS : IN_APP_POLL_MS;
            timerId = setTimeout(() => { check(); schedule(); }, delay);
        };

        check();    // on mount
        schedule(); // start adaptive polling
        window.addEventListener('focus', maybeCheck);
        document.addEventListener('visibilitychange', maybeCheck);
        return () => {
            clearTimeout(timerId);
            window.removeEventListener('focus', maybeCheck);
            document.removeEventListener('visibilitychange', maybeCheck);
        };
    }, []);

    // Login / public screen: informational toast, auto-reloads.
    if (autoReloading) {
        return (
            <div className="vc-banner" role="alert">
                <span className="vc-msg">New version available — updating…</span>
            </div>
        );
    }

    // Inside the app: dismissible prompt with a manual Reload.
    if (updateAvailable && isLoggedIn) {
        return (
            <div className="vc-banner" role="alert">
                <span className="vc-msg">New version available</span>
                <button
                    className="vc-reload"
                    onClick={() => { setReloading(true); reloadToLatest(latestVersion); }}
                    disabled={reloading}
                >
                    Reload
                </button>
                <button
                    className="vc-x"
                    onClick={() => setUpdateAvailable(false)}
                    aria-label="Dismiss"
                >
                    ×
                </button>
            </div>
        );
    }

    return null;
};

export default VersionUpdateNotifier;
